import { useCallback, useMemo } from "react";
import {
  AlertTriangle,
  Award,
  CheckCircle2,
  Clock,
  Droplets,
  GraduationCap,
  Info,
  LineChart as LineChartIcon,
} from "lucide-react";
import {
  Area,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MistralGuidanceChip from "@/components/MistralGuidanceChip";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import { bulletsToActionQueue } from "@/utils/mistralInsightMappers";
import {
  buildApaarClassActionQueue,
  buildApaarClassChartData,
  buildApaarClassHeroStats,
  buildApaarClassMarkdown,
  buildApaarClassPrimaryAction,
  buildApaarClassRiskSignals,
  buildApaarClassStageCards,
  buildApaarClassStatusChips,
  buildApaarClassTableRows,
} from "@/utils/apaarDerived";
import "./ApaarClassAnalysisTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const STAGE_ICONS = {
  "pre-primary": Droplets,
  primary: GraduationCap,
  "upper-primary": LineChartIcon,
  secondary: Award,
};

const SIGNAL_ICONS = {
  red: AlertTriangle,
  orange: Clock,
  green: CheckCircle2,
  "": LineChartIcon,
};

const QUEUE_ICONS = {
  red: AlertTriangle,
  orange: Clock,
  green: CheckCircle2,
};

const ClassDot = ({ cx, cy, payload }) => {
  if (cx == null || cy == null) return null;
  const fill = payload?.isEarly ? "#f59e0b" : "#10b981";
  const stroke = payload?.generation_rate < 60 ? "#ef4444" : "#ffffff";
  return <circle cx={cx} cy={cy} r={7} fill={fill} stroke={stroke} strokeWidth={3} />;
};

const ApaarClassAnalysisTab = ({ overview = {}, classData = [] }) => {
  const hasData = Boolean(classData?.length);

  const buildLocalMarkdown = useCallback(
    () => buildApaarClassMarkdown(overview, classData),
    [overview, classData]
  );

  const scopeKey = useMemo(
    () => JSON.stringify({ c: classData?.length, g: overview?.generation_rate }),
    [classData?.length, overview?.generation_rate]
  );

  const {
    loading: aiLoading,
    pickFromBucket,
    pickHeadline,
    pickPrimaryAction,
    provider,
    isMistral,
    buckets,
  } = useMistralNarrativeOverlay({
    kind: "apaar-class-analysis",
    title: "APAAR Class Analysis",
    buildLocalMarkdown,
    context: { overview, classData },
    enabled: hasData,
    scopeKey,
  });

  const localHeroStats = useMemo(
    () => buildApaarClassHeroStats(classData, overview),
    [classData, overview]
  );
  const heroStats = useMemo(
    () => ({
      ...localHeroStats,
      headline: aiLoading ? localHeroStats.headline : pickHeadline(localHeroStats.headline),
      narrative: aiLoading
        ? localHeroStats.narrative
        : pickFromBucket(localHeroStats.narrative, "insights", 1) || localHeroStats.narrative,
    }),
    [localHeroStats, aiLoading, pickHeadline, pickFromBucket]
  );
  const statusChips = useMemo(() => {
    const chips = buildApaarClassStatusChips(classData, overview);
    if (isMistral && !aiLoading) {
      return [{ label: "Mistral AI Assisted", className: "purple" }, ...chips];
    }
    return chips;
  }, [classData, overview, isMistral, aiLoading]);
  const localPrimaryAction = useMemo(() => buildApaarClassPrimaryAction(classData), [classData]);
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const stageCards = useMemo(() => buildApaarClassStageCards(classData), [classData]);
  const chartData = useMemo(() => buildApaarClassChartData(classData), [classData]);
  const riskSignals = useMemo(() => buildApaarClassRiskSignals(classData), [classData]);
  const tableRows = useMemo(() => buildApaarClassTableRows(classData), [classData]);
  const localActionQueue = useMemo(() => buildApaarClassActionQueue(classData), [classData]);
  const actionQueue = useMemo(() => {
    if (aiLoading) return localActionQueue;
    const aiActions = bulletsToActionQueue(parseInsightBullets(buckets.actions), localActionQueue);
    return aiActions?.length ? aiActions : localActionQueue;
  }, [aiLoading, buckets.actions, localActionQueue]);

  return (
    <div className="apc-class-tab" data-testid="apaar-class-analysis">
      <section className="apc-hero-card" id="apc-class-hero">
        <div className="apc-hero-layout">
          <div>
            <div className="apc-health-main">
              <div
                className="apc-gauge"
                style={heroStats.gaugeStyle}
                aria-label={`Overall class generation rate ${heroStats.districtRate} percent`}
              >
                <div>
                  <strong>{heroStats.districtRate}%</strong>
                  <span>Gen Rate</span>
                </div>
              </div>
              <div className="apc-health-copy">
                <h2>{toTitleCase(heroStats.headline)}</h2>
                <p>{heroStats.narrative}</p>
                <div className="apc-status-row" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                  <MistralGuidanceChip provider={provider} loading={aiLoading} />
                  {statusChips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`apc-status-chip ${chip.className}`.trim()}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="apc-summary-note">
              <div className="apc-icon-box orange">
                <Droplets size={22} />
              </div>
              <div>
                <b>Primary class action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="apc-metric-grid" aria-label="Class analysis KPIs">
            {heroStats.kpis.map((card) => (
              <article key={card.label} className={`apc-metric-card ${card.cardClass}`.trim()}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="apc-section-title" id="apc-stages">
        <div>
          <h2>Stage-Level Snapshot</h2>
          <p>
            Class data is grouped into school stages so district officers can instantly identify
            whether the problem is concentrated in pre-primary, primary, upper primary or secondary
            classes.
          </p>
        </div>
        <button
          type="button"
          className="apc-section-action"
          onClick={() => scrollToId("apc-class-table")}
        >
          View detailed table →
        </button>
      </div>

      <section className="apc-stage-grid" aria-label="Stage-level snapshot">
        {stageCards.map((stage) => {
          const Icon = STAGE_ICONS[stage.id] ?? GraduationCap;
          return (
            <article key={stage.id} className="apc-stage-card">
              <div className="apc-stage-head">
                <div className={`apc-icon-box ${stage.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`apc-status-dot ${stage.dotClass}`.trim()} />
              </div>
              <h3>{toTitleCase(stage.title)}</h3>
              <div className="apc-stage-score">
                <strong>{stage.avgGen}</strong>
                <span>% Avg Gen</span>
              </div>
              <div className="apc-progress-line">
                <i style={{ width: `${stage.progress}%`, background: stage.progressColor }} />
              </div>
              <div className="apc-stage-meta">
                <span>{stage.students} students</span>
                <span>{stage.pending} pending</span>
              </div>
            </article>
          );
        })}
      </section>

      <div className="apc-section-title" id="apc-analytics">
        <div>
          <h2>Generation Trend and Class-Level Risk</h2>
          <p>
            The chart highlights the early-grade dip and the stable performance from Class 3 onwards.
            The right panel turns the trend into practical alerts.
          </p>
        </div>
        <button
          type="button"
          className="apc-section-action"
          onClick={() => scrollToId("apc-actions")}
        >
          View actions →
        </button>
      </div>

      <section className="apc-analytics-grid">
        <article className="apc-panel">
          <div className="apc-panel-head">
            <div>
              <h3>Class-Wise APAAR Generation Rate</h3>
              <p>Generation rate from PP3 to Class 12 with stage-level colour cues.</p>
            </div>
            <span className="apc-pill">Trend</span>
          </div>
          <div className="apc-line-chart" role="img" aria-label="Class-wise APAAR generation line chart">
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="#dfe8f7" strokeDasharray="3 5" vertical={false} />
                <XAxis
                  dataKey="class"
                  tick={{ fill: "#66758f", fontSize: 11, fontWeight: 800 }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={56}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  tick={{ fill: "#66758f", fontSize: 11, fontWeight: 800 }}
                  axisLine={false}
                  tickLine={false}
                  width={36}
                />
                <Tooltip
                  formatter={(value) => [`${value}%`, "Gen Rate"]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid rgba(37,99,235,.14)",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="generation_rate"
                  stroke="none"
                  fill="rgba(16,185,129,.10)"
                />
                <Line
                  type="monotone"
                  dataKey="generation_rate"
                  stroke="#10b981"
                  strokeWidth={4}
                  dot={<ClassDot />}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="apc-panel">
          <div className="apc-panel-head">
            <div>
              <h3>Class Risk Signals</h3>
              <p>Focused alerts generated from the class trend.</p>
            </div>
            <span className="apc-pill">Priority</span>
          </div>
          <div className="apc-insight-list">
            {riskSignals.map((item) => {
              const Icon = SIGNAL_ICONS[item.iconClass] ?? LineChartIcon;
              return (
                <div key={item.title} className="apc-insight-item">
                  <div className={`apc-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                  <span className={`apc-insight-value ${item.valueClass}`.trim()}>
                    {item.value}
                  </span>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <div className="apc-section-title" id="apc-class-table">
        <div>
          <h2>Class-Wise Breakdown and Recommended Actions</h2>
          <p>
            The detailed table keeps the original metrics but adds progress bars, risk badges and
            clear next actions for each grade band.
          </p>
        </div>
        <button
          type="button"
          className="apc-section-action"
          onClick={() => scrollToId("apc-class-hero")}
        >
          Back to top ↑
        </button>
      </div>

      <section className="apc-two-column" id="apc-actions">
        <article className="apc-panel apc-table-card">
          <div className="apc-table-head">
            <div>
              <h3>Class-Wise APAAR Status</h3>
              <p>Students, generated, pending and not-applied percentage by class.</p>
            </div>
            <span className="apc-pill">{classData.length} Classes</span>
          </div>
          <div className="apc-table-wrap">
            <table aria-label="Class-wise APAAR breakdown">
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Students</th>
                  <th>Generated</th>
                  <th>Gen %</th>
                  <th>Pending</th>
                  <th>Not Applied %</th>
                  <th>Risk</th>
                  <th>Next Action</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={row.classKey}>
                    <td>{row.class}</td>
                    <td>{row.total_students}</td>
                    <td>{row.total_generated}</td>
                    <td className="apc-score">
                      <div className="apc-mini-progress">
                        <i
                          className={row.progressClass}
                          style={{ width: `${row.progressWidth}%` }}
                        />
                      </div>
                      {row.generation_rate}%
                    </td>
                    <td>{row.pending}</td>
                    <td>
                      <span className={`apc-tag ${row.notAppliedTagClass}`.trim()}>
                        {row.not_applied_pct}
                      </span>
                    </td>
                    <td>
                      <span className={`apc-tag ${row.tagClass}`.trim()}>{row.risk}</span>
                    </td>
                    <td>{row.nextAction}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="apc-panel">
          <div className="apc-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Three focused actions for class-wise improvement.</p>
            </div>
            <span className="apc-pill">48-hour plan</span>
          </div>
          <div className="apc-action-card">
            {actionQueue.map((item) => {
              const Icon = QUEUE_ICONS[item.iconClass] ?? Info;
              return (
                <div key={item.title} className="apc-action-item">
                  <div className={`apc-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="apc-action-footer">
              <b>Next review recommendation</b>
              <span>
                Review PP1–Class 1 movement separately from higher classes. A single average hides
                the actual early-grade issue.
              </span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default ApaarClassAnalysisTab;
